import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { styled } from '@this/constants/themes';
import { ByMedia } from '@this/shared/ui/other/by_media';
import HeaderContentSm from './header_content_sm';
import { ItemIcon, ReportIcon, ApprovalIcon, SettingIcon } from './header_icons';
import type User from '../../../domain/user/user';

interface Props {
  user: User | null | undefined;
}

const ExpensesHeader: React.FC<Props> = ({ user }) => {
  const location = useLocation();
  const [path, setPath] = useState(location.pathname);
  const history = useHistory();

  useEffect(() => {
    return history.listen(_location => {
      setPath(_location.pathname);
    });
  }, [history]);

  const icon = (_path: string, iconType: 'item' | 'report' | 'approval' | 'setting') => {
    const isActive = path.startsWith(_path);
    switch (iconType) {
      case 'item':
        return <ItemIcon isActive={isActive} />;
      case 'report':
        return <ReportIcon isActive={isActive} />;
      case 'approval':
        return <ApprovalIcon isActive={isActive} />;
      case 'setting':
        return <SettingIcon isActive={isActive} />;
      default:
        return null;
    }
  };
  const title = (_path: string, label: string) => {
    const className = path.startsWith(_path) ? 'active' : '';
    return <span className={className}>{label}</span>;
  };

  return (
    <Header>
      <HeaderWrapper>
        <HeaderContent>
          <HeaderLogoWrapper>
            <HeaderLogo href="/biztra/report_items">
              <img src="/images/expenses/AIE_572px.png" height="19" alt="AI EXPENSE" />
            </HeaderLogo>
          </HeaderLogoWrapper>
          <HeaderLinkArea>
            {user && (
              <ByMedia>
                {matches =>
                  matches.sm ? (
                    <HeaderContentSm user={user} />
                  ) : (
                    <>
                      <HeaderLink>
                        <Link to="/biztra/report_items">
                          {icon('/biztra/report_items', 'item')}
                          {title('/biztra/report_items', '登録')}
                        </Link>
                      </HeaderLink>
                      <HeaderLink>
                        <Link to="/biztra/reports">
                          {icon('/biztra/reports', 'report')}
                          {title('/biztra/reports', '申請')}
                        </Link>
                      </HeaderLink>
                      {(user?.isOrganizationAdmin || user?.is_expenses_approver) && (
                        <HeaderLink>
                          <Link to="/biztra/approvals">
                            {icon('/biztra/approvals', 'approval')}
                            {title('/biztra/approvals', '承認')}
                          </Link>
                        </HeaderLink>
                      )}
                      {user?.isOrganizationAdmin && (
                        <HeaderLink>
                          <Link to="/biztra/organization">
                            {icon('/biztra/organization', 'setting')}
                            {title('/biztra/organization', '管理')}
                          </Link>
                        </HeaderLink>
                      )}
                    </>
                  )
                }
              </ByMedia>
            )}
          </HeaderLinkArea>
        </HeaderContent>
      </HeaderWrapper>
    </Header>
  );
};

export const headerTxtColor = '#efedea';

export const headerHeight = '50px';

const contentPadding = '16px';

const Header = styled.header`
  height: ${headerHeight};
`;

const HeaderWrapper = styled.div`
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
  background-color: black;
  height: ${headerHeight};
  padding: ${contentPadding} 0;
  @media screen and (max-width: 767px) {
    padding: unset;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 10px;
  @media screen and (max-width: 767px) {
    margin: 0;
  }
`;

const HeaderLogoWrapper = styled.h1`
  @media screen and (max-width: 767px) {
    padding: 16px 0;
  }
`;

const HeaderLogo = styled.a`
  display: flex;
  z-index: 9999;
  img {
    height: calc(${headerHeight} - (${contentPadding} * 2));
  }
`;

const HeaderLinkArea = styled.ul`
  display: flex;
  height: 100%;
`;

export const HeaderLink = styled.li`
  margin-right: 24px;

  &:last-child {
    margin-right: 0;
  }

  a {
    font-size: 16px;
    display: flex;
    align-items: center;
    height: 100%;
    line-height: 100%;
    color: ${headerTxtColor};
    font-weight: bold;
  }

  img,
  svg {
    display: inline-block;
    height: 15px;
    margin-right: 6px;
  }

  span {
    display: inline-block;

    &.active {
      color: #53c4f1;
    }
  }

  @media screen and (max-width: 767px) {
    margin-right: 0;

    a {
      display: block;
      padding: 20px 35vw;
    }
  }
`;

export default ExpensesHeader;
